import type {
  DocumentResult,
  LearningSessionSummary,
  OperatorCorrection,
} from '../shared/desktop-api';

/**
 * Teaching history as people remember it: one sitting of work, not one row per
 * field. Operator answers close together in time become one group; training
 * runs are their own entries. Nothing here claims what an answer taught — the
 * journey owns progress, and this view only restates saved records.
 */
export type HistoryDocument = Pick<DocumentResult, 'id' | 'sourceName' | 'documentType'>;
/** `confirmed` and `reopened` follow the document's newest confirmation or withdrawal in the sitting. */
export type GroupDocument = {
  id: string;
  label: string;
  typeName: string | null;
  available: boolean;
  confirmed: boolean;
  reopened: boolean;
  checks: number;
  fixes: number;
};
export type AnswerGroup = {
  kind: 'answers';
  id: string;
  at: string;
  title: string;
  summary: string;
  events: OperatorCorrection[];
  confirmedDocuments: number;
  reopenedDocuments: number;
  fixes: number;
  checks: number;
  documents: GroupDocument[];
  typeCounts: { name: string; count: number }[];
  /** The one correction, when the group is a single fix. */
  single: OperatorCorrection | null;
  /** The read stopped inside this sitting: its counts are a floor ("at least"), not the whole sitting. */
  partial: boolean;
};
export type TrainingGroup = {
  kind: 'training';
  id: string;
  at: string;
  title: string;
  summary: string;
  session: LearningSessionSummary;
  kept: string[];
  unchanged: string[];
  /** The status says the run ended while its record is still live: "Finishing…" or "Stopped · report not saved". */
  closing?: string;
};
/** Sessions the status says have ended while their records are still live; `reportLost`: the engine said its final save failed. */
export type ClosingSessions = { ids: ReadonlySet<string>; reportLost: boolean };
export type HistoryGroup = AnswerGroup | TrainingGroup;

export const HISTORY_SESSION_GAP_MINUTES = 20;
/** How many of the newest answers History reads (the engine's most). A full read may have cut its oldest sitting. */
export const HISTORY_ANSWER_LIMIT = 500;

const time = (iso: string) => {
  const value = Date.parse(iso);
  return Number.isFinite(value) ? value : 0;
};
const count = (value: number, one: string, many = `${one}s`) =>
  `${value.toLocaleString()} ${value === 1 ? one : many}`;
const isConfirmation = (entry: OperatorCorrection) => entry.kind === 'document-confirmed';
/** The engine's undo of a confirmation; a newer one than the confirmation leaves the document unconfirmed. */
const isWithdrawal = (entry: OperatorCorrection) => entry.kind === 'confirmation-withdrawn';
const isFix = (entry: OperatorCorrection) => entry.kind.endsWith('-correction');
const isCheck = (entry: OperatorCorrection) => entry.kind.endsWith('-verified');

function correctionTitle(entry: OperatorCorrection) {
  const name = entry.fieldName?.trim();
  if (entry.kind === 'document-type-correction' || name === 'Document type')
    return 'You corrected the document type';
  if (entry.kind === 'line-item-correction' || name === 'Line items')
    return 'You corrected line items';
  return name ? `You corrected ${name}` : 'You corrected a value';
}

function answerGroup(
  events: OperatorCorrection[],
  documents: ReadonlyMap<string, HistoryDocument>,
  partial = false,
): AnswerGroup {
  const ordered = [...events].sort((a, b) => time(b.occurredAt) - time(a.occurredAt));
  const byDocument = new Map<string, GroupDocument>();
  const settled = new Set<string>();
  for (const entry of ordered) {
    const source = documents.get(entry.documentId);
    const row = byDocument.get(entry.documentId) ?? {
      id: entry.documentId,
      label: source?.sourceName || 'Earlier document',
      typeName: source?.documentType || null,
      available: Boolean(source),
      confirmed: false,
      reopened: false,
      checks: 0,
      fixes: 0,
    };
    // Newest first: the first confirmation or withdrawal met is where the document was left.
    if (isConfirmation(entry) || isWithdrawal(entry)) {
      if (!settled.has(entry.documentId)) {
        settled.add(entry.documentId);
        row.confirmed = isConfirmation(entry);
        row.reopened = isWithdrawal(entry);
      }
    } else if (isFix(entry)) row.fixes += 1;
    else if (isCheck(entry)) row.checks += 1;
    byDocument.set(entry.documentId, row);
  }
  const rows = [...byDocument.values()];
  const confirmedDocuments = rows.filter((row) => row.confirmed).length;
  const reopenedDocuments = rows.filter((row) => row.reopened).length;
  const fixes = ordered.filter(isFix).length,
    checks = ordered.filter(isCheck).length;
  const types = new Map<string, number>();
  for (const row of rows)
    if (row.typeName) types.set(row.typeName, (types.get(row.typeName) ?? 0) + 1);
  const typeCounts = [...types]
    .map(([name, value]) => ({ name, count: value }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const single = ordered.length === 1 && isFix(ordered[0]) ? ordered[0] : null;
  // A sitting the read stopped inside is counted as far as it was read, and says so.
  const atLeast = partial ? 'at least ' : '';
  const title = confirmedDocuments
    ? `You confirmed ${atLeast}${count(confirmedDocuments, 'document')}`
    : single
      ? correctionTitle(single)
      : fixes
        ? `You made ${atLeast}${count(fixes, 'correction')}`
        : // Only the documents with a check: one whose only answer was a reopen was not checked.
          checks
          ? `You checked ${atLeast}${count(rows.filter((row) => row.checks > 0).length, 'document')}`
          : `You reopened ${atLeast}${count(reopenedDocuments || rows.length, 'document')}`;
  const breakdown = typeCounts.map((entry) => `${entry.name} (${entry.count})`).join(', ');
  const summary = [
    ...(single
      ? [
          rows[0]?.label,
          single.before != null || single.after != null
            ? `${single.before ?? 'blank'} → ${single.after ?? 'blank'}`
            : null,
        ]
      : [
          breakdown || count(rows.length, 'document'),
          confirmedDocuments && fixes ? `${fixes} fixed` : null,
          reopenedDocuments && (confirmedDocuments || fixes || checks)
            ? `${reopenedDocuments} reopened`
            : null,
        ]),
    partial ? 'earlier answers in this sitting aren’t shown' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    kind: 'answers',
    id: `answers:${ordered.at(-1)?.eventId ?? ordered[0]?.eventId}`,
    at: ordered[0]?.occurredAt ?? '',
    title,
    summary,
    events: ordered,
    confirmedDocuments,
    reopenedDocuments,
    fixes,
    checks,
    documents: rows,
    typeCounts,
    single,
    partial,
  };
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
function trainingGroup(session: LearningSessionSummary, closing?: ClosingSessions): TrainingGroup {
  const kept = session.lastCheckpoint?.promotions ?? [];
  const unchanged = session.lastCheckpoint?.refusals ?? [];
  const ending = closing?.ids.has(session.id)
    ? closing.reportLost
      ? 'Stopped · report not saved'
      : 'Finishing…'
    : undefined;
  // A resting session is still live: it waits for new answers, and nothing it has done yet is a result.
  const title = ending
    ? closing?.reportLost
      ? 'Training stopped · its report wasn’t saved'
      : 'Training is finishing'
    : session.state === 'running' || session.state === 'stopping'
      ? 'Training is running'
      : session.state === 'resting'
        ? 'Training is waiting for new answers'
        : kept.length
          ? `Training kept ${count(kept.length, 'improvement')}`
          : session.state === 'failed' || session.state === 'interrupted'
            ? 'Training stopped early'
            : session.tried > 0
              ? 'Training kept your current setup'
              : 'Training ran';
  const summary = [
    `${count(session.tried, 'approach', 'approaches')} tried`,
    session.operationalFailures
      ? `${count(session.operationalFailures, 'check')} had issues`
      : null,
    session.mode === 'scheduled' ? 'scheduled' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    kind: 'training',
    id: `training:${session.id}`,
    at: session.startedAt,
    title,
    summary,
    session,
    kept,
    unchanged,
    ...(ending ? { closing: ending } : {}),
  };
}

/** Newest first. Answers split into sittings wherever a quiet gap appears. `limited`: the answers are only the
 * newest ones the engine returned, so the oldest sitting may be cut short; it is left out rather than miscounted. */
export function groupLearningHistory(
  corrections: readonly OperatorCorrection[],
  sessions: readonly LearningSessionSummary[],
  documents: readonly HistoryDocument[],
  {
    gapMinutes = HISTORY_SESSION_GAP_MINUTES,
    limited = false,
    closing,
  }: { gapMinutes?: number; limited?: boolean; closing?: ClosingSessions } = {},
): HistoryGroup[] {
  const lookup = new Map(documents.map((document) => [document.id, document]));
  const ordered = [...corrections]
    .filter((entry) => time(entry.occurredAt) > 0)
    .sort((a, b) => time(b.occurredAt) - time(a.occurredAt));
  const groups: HistoryGroup[] = [];
  let current: OperatorCorrection[] = [];
  for (const entry of ordered) {
    const previous = current.at(-1);
    if (previous && time(previous.occurredAt) - time(entry.occurredAt) > gapMinutes * 60_000) {
      groups.push(answerGroup(current, lookup));
      current = [];
    }
    current.push(entry);
  }
  // A single sitting is all there is to show, so it stays, counted as "at least"; the panel says older
  // answers aren't shown.
  if (current.length && !(limited && groups.length > 0))
    groups.push(answerGroup(current, lookup, limited));
  groups.push(...sessions.map((session) => trainingGroup(session, closing)));
  return groups.sort((a, b) => time(b.at) - time(a.at));
}

/** "Today 2:14 PM", "Yesterday 4:31 PM", "Mon 3:10 PM", else "Sep 20". */
export function historyWhen(iso: string, now = new Date(), locale?: string) {
  const at = new Date(iso);
  if (!Number.isFinite(at.getTime())) return '';
  const day = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const days = Math.round((day(now) - day(at)) / 86_400_000);
  const clock = at.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  if (days === 0) return `Today ${clock}`;
  if (days === 1) return `Yesterday ${clock}`;
  if (days > 1 && days < 7)
    return `${at.toLocaleDateString(locale, { weekday: 'short' })} ${clock}`;
  return at.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}
